import { useContext, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  UncontrolledDropdown,
  Modal,
  ModalBody,
  DropdownItem,
  Form,
  Badge,
  Card,
  CardHeader,
  CardBody,
} from "reactstrap";
import { isEmpty } from "lodash";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import Button from "./../../../components/Button/Button";
import Icon from "../../../components/Icon/Icon";
import ErrorMessage from "../../../helpers/ErrorMessage/ErrorMessage";
import PaginationComponent from "./../../../components/Pagination/Pagination";
import TableFilter from "../../../components/TableFilter/TableFilter";
import UserAvatar from "../../../components/UserAvatar/UserAvatar";
import PageHeader from "../../../components/PageHeader/PageHeader";
import SearchTable from "../../../components/SearchTable/SearchTable";
import AvatarCropper from "./../../../components/AvatarCropper/AvatarCropper";
import UserInfoItem from "../../../components/UserInfoItem/UserInfoItem";
import VerifiedBadge from "./../../../components/VerifiedBadge/VerifiedBadge";
import DateTimePicker from "../../../components/DateTimePicker/DateTimePicker";
import { userAdminContext } from "../../../context/AdminContext/UserAdminContext/UserAdminContext";
import Currency, {
  ConvertCurrency,
  ConvertCurrencyToText,
} from "./../../../helpers/Currency/Currency";
import { Block } from "./../../../components/Block/Block";
import {
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
} from "./../../../components/DataTable/DataTable";
import { Col, Row } from "./../../../components/Grid/Grid";
import {
  convertDate,
  convertRoleToText,
  convertStringToBoolean,
  sumWallets,
} from "../../../utils";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";
import {
  changeUserPasswordValidation,
  createTransactionForAdminValidation,
  createUserForAdminValidation,
  editUserForAdminValidation,
} from "../../../utils/validation/validation";
import {
  convertRoleToTitle,
  convertTransactionTypeToText,
  createTransactionTypeItems,
  userRoleItems,
  adminPermissions,
  agentPermissions,
  categoryItems,
  convertCategoryToText,
} from "../../../utils/enum";

const UsersAdmin = () => {
  const navigate = useNavigate();

  const loader = useSelector((state) => state.loader);

  const { agent } = useParams();

  const {
    users,
    user,
    setUser,
    filter,
    setFilter,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    role,
    pageCount,
    itemsCount,
    setItemsCount,
    pageNumber,
    setPageNumber,
    isShowModal,
    handleShowModal,
    handleHideModal,
    handleGetUsers,
    handleCreateUser,
    handleEditUser,
    handleChangeUserActive,
    handleDeleteUser,
    handleVerifyUserPhoneNumber,
    handleVerifyUserEmail,
    handleUploadFile,
  } = useContext(userAdminContext);

  useEffect(() => {
    handleGetUsers();
  }, [start, end, pageNumber, role, itemsCount, sort, agent]);

  // Validation
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      permissions: "111111",
    },
    resolver: yupResolver(
      isEmpty(user) ? createUserForAdminValidation : editUserForAdminValidation
    ),
  });

  const isAdmin = role === userRoleItems[0];
  const isAgent = role === userRoleItems[1];
  const isCustomer = role === userRoleItems[2];
  const isReception = role === userRoleItems[3];
  const isShop = role === userRoleItems[4];

  const permissions = isAdmin ? adminPermissions : agentPermissions;

  // Change Permission
  const handleChangePermission = async (index, value) => {
    const copy = [...watch("permissions")];
    copy[index] = value;
    setValue("permissions", copy.toString().replaceAll(",", ""));
  };

  return (
    <>
      <SiteTitle title={convertRoleToTitle(role)} />

      <PageHeader title={convertRoleToTitle(role)} />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
      />

      <Block>
        <DataTable className="card-stretch">
          <SearchTable
            firstItem={
              !isCustomer && (
                <Button
                  color="primary"
                  className="btn-icon"
                  onClick={() => {
                    handleShowModal();
                    reset();
                  }}
                >
                  <Icon name="plus" />
                </Button>
              )
            }
            filterPlaceholder="نام، نام خانوادگی، شماره موبایل، کد ملی و..."
            filter={filter}
            setFilter={setFilter}
            onSearch={handleGetUsers}
          />
          <DataTableBody>
            <DataTableHead>
              <DataTableRow>
                <span className="sub-text">#</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">{convertRoleToText(role)}</span>
              </DataTableRow>
              {isCustomer && (
                <DataTableRow size="md">
                  <span className="sub-text">نماینده</span>
                </DataTableRow>
              )}
              <DataTableRow size="mb">
                <span className="sub-text">شماره همراه</span>
              </DataTableRow>
              <DataTableRow size="xxl">
                <span className="sub-text">کد ملی</span>
              </DataTableRow>
              {!isAdmin && (
                <DataTableRow size="lg">
                  <span className="sub-text">کیف پول</span>
                </DataTableRow>
              )}
              <DataTableRow size="xxl">
                <span className="sub-text">تاریخ ثبت نام</span>
              </DataTableRow>
              <DataTableRow size="md">
                <span className="sub-text">وضعیت</span>
              </DataTableRow>
              <DataTableRow />
            </DataTableHead>
            {/*Head*/}
            {users.length > 0
              ? users.map((item, index) => {
                  return (
                    <DataTableItem key={item._id}>
                      <DataTableRow>
                        <span>{index + 1}</span>
                      </DataTableRow>
                      <DataTableRow>
                        <div className="user-card">
                          <UserAvatar
                            {...(item?.avatar
                              ? { image: item?.avatar }
                              : {
                                  icon: "user-alt",
                                })}
                          />
                          <UserInfoItem
                            title={`${item.firstName} ${item.lastName}`}
                            subTitle={item.email}
                          />
                        </div>
                      </DataTableRow>
                      {isCustomer && (
                        <DataTableRow size="md">
                          <div className="user-card">
                            <UserAvatar
                              {...(item?.agent?.avatar
                                ? { image: item?.agent?.avatar }
                                : {
                                    icon: "user-alt",
                                  })}
                            />
                            <UserInfoItem
                              title={`${item?.agent?.firstName} ${item?.agent?.lastName}`}
                              subTitle={item?.agent?.email}
                            />
                          </div>
                        </DataTableRow>
                      )}
                      <DataTableRow size="mb">
                        <span>
                          {item.phoneNumber}{" "}
                          <VerifiedBadge
                            isVerified={item?.phoneNumberVerified}
                          />
                        </span>
                      </DataTableRow>
                      <DataTableRow size="xxl">
                        <span>{item.nationalNumber}</span>
                      </DataTableRow>
                      {!isAdmin && (
                        <DataTableRow size="lg">
                          <span>
                            <ConvertCurrency price={sumWallets(item.wallet)} />{" "}
                            <Currency />
                          </span>
                        </DataTableRow>
                      )}
                      <DataTableRow size="xxl">
                        <span>
                          {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm")}
                        </span>
                      </DataTableRow>
                      <DataTableRow size="md">
                        <span
                          className={`badge badge-dot has-bg badge-${
                            item.isActive ? "success" : "danger"
                          }`}
                        >
                          {item.isActive ? "فعال" : "غیرفعال"}
                        </span>
                      </DataTableRow>
                      <DataTableRow className="nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1">
                          <li>
                            <UncontrolledDropdown>
                              <DropdownToggle
                                tag="a"
                                className="dropdown-toggle btn btn-icon btn-trigger"
                              >
                                <Icon name="more-h" />
                              </DropdownToggle>
                              <DropdownMenu
                                left
                                className="dropdown_max_height"
                              >
                                <ul className="link-list-opt no-bdr text-right">
                                  {!isAdmin && !isShop && (
                                    <>
                                      <li
                                        onClick={() =>
                                          navigate(
                                            `/admin/reception-orders/${item._id}/user`
                                          )
                                        }
                                      >
                                        <DropdownItem tag="a" type="button">
                                          <Icon name="list" />
                                          <span>
                                            گزارش سفارشات از پذیرنده{" "}
                                            {convertRoleToText(role)}
                                          </span>
                                        </DropdownItem>
                                      </li>
                                      <li
                                        onClick={() =>
                                          navigate(
                                            `/admin/shop-orders/${item._id}/user`
                                          )
                                        }
                                      >
                                        <DropdownItem tag="a" type="button">
                                          <Icon name="view-list-fill" />
                                          <span>
                                            گزارش سفارشات از فروشگاه{" "}
                                            {convertRoleToText(role)}
                                          </span>
                                        </DropdownItem>
                                      </li>
                                      {!isReception && (
                                        <>
                                          <li
                                            onClick={() =>
                                              navigate(
                                                `/admin/hotel-orders/${item._id}/user`
                                              )
                                            }
                                          >
                                            <DropdownItem tag="a" type="button">
                                              <Icon name="home" />
                                              <span>
                                                گزارش سفارشات هتل{" "}
                                                {convertRoleToText(role)}
                                              </span>
                                            </DropdownItem>
                                          </li>
                                          <li
                                            onClick={() =>
                                              navigate(
                                                `/admin/fly-orders/${item._id}/user`
                                              )
                                            }
                                          >
                                            <DropdownItem tag="a" type="button">
                                              <Icon name="light" />
                                              <span>
                                                گزارش سفارشات پرواز{" "}
                                                {convertRoleToText(role)}
                                              </span>
                                            </DropdownItem>
                                          </li>
                                        </>
                                      )}
                                    </>
                                  )}
                                  <li
                                    onClick={() =>
                                      navigate(`/admin/logs/${item._id}/user`)
                                    }
                                  >
                                    <DropdownItem tag="a" type="button">
                                      <Icon name="user-list" />
                                      <span>
                                        گزارش رویدادهای{" "}
                                        {convertRoleToText(role)}
                                      </span>
                                    </DropdownItem>
                                  </li>
                                  {!isAdmin && (
                                    <li
                                      onClick={() =>
                                        navigate(
                                          `/admin/transactions/${item._id}/user`
                                        )
                                      }
                                    >
                                      <DropdownItem tag="a" type="button">
                                        <Icon name="report-profit" />
                                        <span>تراکنشات</span>
                                      </DropdownItem>
                                    </li>
                                  )}
                                  {isAgent && (
                                    <li
                                      onClick={() =>
                                        navigate(
                                          `/admin/customers/${item._id}/agent`
                                        )
                                      }
                                    >
                                      <DropdownItem tag="a" type="button">
                                        <Icon name="user-circle" />
                                        <span>مشتریان نماینده</span>
                                      </DropdownItem>
                                    </li>
                                  )}
                                  {!isAdmin && (
                                    <li
                                      onClick={() =>
                                        setUser({
                                          ...item,
                                          isTransaction: true,
                                        })
                                      }
                                    >
                                      <DropdownItem tag="a" type="button">
                                        <Icon name="wallet-saving" />
                                        <span>ایجاد تراکنش</span>
                                      </DropdownItem>
                                    </li>
                                  )}
                                  <li
                                    onClick={() => {
                                      reset();
                                      handleShowModal(item, setValue);
                                    }}
                                  >
                                    <DropdownItem tag="a" type="button">
                                      <Icon name="edit-alt-fill" />
                                      <span>ویرایش</span>
                                    </DropdownItem>
                                  </li>
                                  <li
                                    onClick={() =>
                                      setUser({ ...item, isPassword: true })
                                    }
                                  >
                                    <DropdownItem tag="a" type="button">
                                      <Icon name="lock-alt-fill" />
                                      <span>تغییر کلمه عبور</span>
                                    </DropdownItem>
                                  </li>
                                  <li className="divider" />
                                  <li
                                    onClick={() =>
                                      areYouSureMessage(
                                        () =>
                                          handleVerifyUserPhoneNumber(item._id),
                                        `آیا از ${
                                          !item.phoneNumberVerified
                                            ? "تایید"
                                            : "عدم تایید"
                                        } کردن شماره موبایل ${convertRoleToText(
                                          role
                                        )} ${item.firstName} ${
                                          item.lastName
                                        } مطمئنید؟`
                                      )
                                    }
                                  >
                                    <DropdownItem
                                      tag="a"
                                      type="button"
                                      className={`text-${
                                        !item.phoneNumberVerified
                                          ? "success"
                                          : "danger"
                                      }`}
                                    >
                                      <Icon
                                        name={
                                          !item.phoneNumberVerified
                                            ? "check-round"
                                            : "cross-round"
                                        }
                                      />
                                      <span>
                                        {!item.phoneNumberVerified
                                          ? "تایید"
                                          : "عدم تایید"}{" "}
                                        شماره موبایل
                                      </span>
                                    </DropdownItem>
                                  </li>
                                  <li
                                    onClick={() =>
                                      areYouSureMessage(
                                        () => handleVerifyUserEmail(item._id),
                                        `آیا از ${
                                          !item.emailVerified
                                            ? "تایید"
                                            : "عدم تایید"
                                        } کردن شماره موبایل ${convertRoleToText(
                                          role
                                        )} ${item.firstName} ${
                                          item.lastName
                                        } مطمئنید؟`
                                      )
                                    }
                                  >
                                    <DropdownItem
                                      tag="a"
                                      type="button"
                                      className={`text-${
                                        !item.emailVerified
                                          ? "success"
                                          : "danger"
                                      }`}
                                    >
                                      <Icon
                                        name={
                                          !item.emailVerified
                                            ? "check-circle"
                                            : "cross-circle"
                                        }
                                      />
                                      <span>
                                        {!item.emailVerified
                                          ? "تایید"
                                          : "عدم تایید"}{" "}
                                        پست الکترونیک
                                      </span>
                                    </DropdownItem>
                                  </li>
                                  <li
                                    onClick={() =>
                                      areYouSureMessage(
                                        () => handleChangeUserActive(item._id),
                                        `آیا از ${
                                          !item.isActive ? "فعال" : "غیرفعال"
                                        } کردن ${convertRoleToText(role)} ${
                                          item.firstName
                                        } ${item.lastName} مطمئنید؟`
                                      )
                                    }
                                  >
                                    <DropdownItem
                                      tag="a"
                                      type="button"
                                      className={`text-${
                                        !item.isActive ? "success" : "danger"
                                      }`}
                                    >
                                      <Icon
                                        name={
                                          !item.isActive
                                            ? "user-check-fill"
                                            : "user-cross-fill"
                                        }
                                      />
                                      <span>
                                        {!item.isActive ? "فعال" : "غیرفعال"}
                                      </span>
                                    </DropdownItem>
                                  </li>
                                  <li
                                    onClick={() =>
                                      areYouSureMessage(
                                        () => handleDeleteUser(item._id),
                                        `آیا از حذف ${convertRoleToText(
                                          role
                                        )} ${item.firstName} ${
                                          item.lastName
                                        } مطمئنید؟`
                                      )
                                    }
                                  >
                                    <DropdownItem
                                      tag="a"
                                      type="button"
                                      className="text-danger"
                                    >
                                      <Icon name="trash-fill" />
                                      <span>حذف</span>
                                    </DropdownItem>
                                  </li>
                                </ul>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </li>
                        </ul>
                      </DataTableRow>
                    </DataTableItem>
                  );
                })
              : null}
          </DataTableBody>
          <div className="card-inner">
            {users.length > 0 ? (
              <PaginationComponent
                pageCount={pageCount}
                currentPage={pageNumber}
                setPageNumber={setPageNumber}
              />
            ) : (
              <div className="text-center">
                <span className="text-silent">داده ای برای نمایش یافت نشد</span>
              </div>
            )}
          </div>
        </DataTable>
      </Block>

      <Modal
        isOpen={isShowModal}
        toggle={handleHideModal}
        className="modal-dialog-centered"
        size="lg"
      >
        <ModalBody>
          <span type="button" onClick={handleHideModal} className="close">
            <Icon name="cross-sm" />
          </span>
          <div className="p-2">
            <h5 className="title">
              {isEmpty(user)
                ? `افزودن ${convertRoleToText(role)} جدید`
                : `ویرایش ${convertRoleToText(role)}`}
            </h5>
            <br />
            <Form
              className="is-alter"
              onSubmit={handleSubmit(
                isEmpty(user) ? handleCreateUser : handleEditUser
              )}
            >
              <AvatarCropper
                onRemove={() => setValue("avatar", "")}
                value={watch("avatar")}
                onChange={(file) =>
                  handleUploadFile(file, (e) => setValue("avatar", e))
                }
              />

              <Row className="gy-4">
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="firstName">
                      نام
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        placeholder="نام را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("firstName")}
                      />
                      <ErrorMessage message={errors.firstName?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="lastName">
                      نام خانوادگی
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        placeholder="نام خانوادگی را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("lastName")}
                      />
                      <ErrorMessage message={errors.lastName?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="phoneNumber">
                      شماره موبایل{" "}
                      {!isEmpty(user) && (
                        <VerifiedBadge isVerified={user?.phoneNumberVerified} />
                      )}
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="number"
                        placeholder="شماره موبایل را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("phoneNumber")}
                      />
                      <ErrorMessage message={errors.phoneNumber?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="nationalNumber">
                      کدملی
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="number"
                        placeholder="کدملی را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("nationalNumber")}
                      />
                      <ErrorMessage message={errors.nationalNumber?.message} />
                    </div>
                  </FormGroup>
                </Col>{" "}
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="code">
                      کد
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        placeholder="کد را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("code")}
                      />
                      <ErrorMessage message={errors.code?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="birthDate">
                      تاریخ تولد
                    </label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        render={({ field: { onChange, value, onBlur } }) => (
                          <DateTimePicker
                            placeholder="تاریخ تولد را وارد نمایید"
                            initValue={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            className="form-control-lg form-control"
                          />
                        )}
                        name="birthDate"
                      />
                      <ErrorMessage message={errors.birthDate?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="post">
                      سمت
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        placeholder="سمت را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("post")}
                      />
                      <ErrorMessage message={errors.post?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="numberOfCovered">
                      تعداد افراد تحت پوشش
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="number"
                        placeholder="تعداد افراد تحت پوشش را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("numberOfCovered")}
                      />
                      <ErrorMessage message={errors.numberOfCovered?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <label className="form-label" htmlFor="email">
                      پست الکترونیک{" "}
                      {!isEmpty(user) && (
                        <VerifiedBadge isVerified={user?.emailVerified} />
                      )}
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="email"
                        placeholder="پست الکترونیک را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("email")}
                      />
                      <ErrorMessage message={errors.email?.message} />
                    </div>
                  </FormGroup>
                </Col>
                {isEmpty(user) && (
                  <>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="password">
                          کلمه عبور
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="password"
                            placeholder="کلمه عبور را وارد نمایید"
                            className="form-control-lg form-control"
                            {...register("password")}
                          />
                          <ErrorMessage message={errors.password?.message} />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="confirmPassword">
                          تکرار کلمه عبور
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="password"
                            placeholder="تکرار کلمه عبور را وارد نمایید"
                            className="form-control-lg form-control"
                            {...register("confirmPassword")}
                          />
                          <ErrorMessage
                            message={errors.confirmPassword?.message}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                  </>
                )}
                {watch("permissions") && !isCustomer && (
                  <Col size="12">
                    <Card>
                      <CardHeader className="text-center">دسترسی ها</CardHeader>
                      <CardBody>
                        <Row>
                          {Object.keys(permissions).map((item, index) => (
                            <Col key={item} md="6" className="mb-3">
                              <div className="custom-control custom-switch">
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  checked={convertStringToBoolean(
                                    watch("permissions")[index]
                                  )}
                                  onChange={(e) =>
                                    handleChangePermission(
                                      index,
                                      +e.target.checked
                                    )
                                  }
                                  id={item}
                                />
                                <label
                                  className="custom-control-label"
                                  htmlFor={item}
                                >
                                  {permissions[item]}
                                </label>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                )}
                <Col size="12">
                  <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                    <li>
                      <Button
                        color="primary"
                        size="lg"
                        type="submit"
                        loading={loader}
                      >
                        {isEmpty(user) ? "افزودن" : "ویرایش"} کاربر
                      </Button>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={handleHideModal}
                        className="link link-light"
                      >
                        انصراف
                      </button>
                    </li>
                  </ul>
                </Col>
              </Row>
            </Form>
          </div>
        </ModalBody>
      </Modal>

      <ChangePassword />
      <CreateTransaction />
    </>
  );
};

export default UsersAdmin;

const ChangePassword = () => {
  const loader = useSelector((state) => state.loader);

  const { user, setUser, handleChangeUserPassword } =
    useContext(userAdminContext);

  // Validation
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(changeUserPasswordValidation),
  });

  useEffect(() => {
    if (user.isPassword) reset();
  }, [user]);

  return (
    <Modal
      isOpen={user.isPassword}
      className="modal-dialog-centered"
      size="md"
      toggle={() => setUser({})}
    >
      <ModalBody>
        <span type="button" onClick={() => setUser({})} className="close">
          <Icon name="cross-sm" />
        </span>
        <div className="p-2">
          <h5 className="title">
            ویرایش کلمه عبور {user?.firstName} {user?.lastName}
          </h5>
          <br />
          <Form
            className="is-alter"
            onSubmit={handleSubmit(handleChangeUserPassword)}
          >
            <FormGroup>
              <label className="form-label" htmlFor="password">
                کلمه عبور <Badge color="primary">جدید</Badge>
              </label>
              <div className="form-control-wrap">
                <input
                  type="password"
                  placeholder="کلمه عبور را وارد نمایید"
                  className="form-control-lg form-control"
                  {...register("password")}
                />
                <ErrorMessage message={errors.password?.message} />
              </div>
            </FormGroup>
            <FormGroup>
              <label className="form-label" htmlFor="confirmPassword">
                تکرار کلمه عبور <Badge color="primary">جدید</Badge>
              </label>
              <div className="form-control-wrap">
                <input
                  type="password"
                  placeholder="تکرار کلمه عبور را وارد نمایید"
                  className="form-control-lg form-control"
                  {...register("confirmPassword")}
                />
                <ErrorMessage message={errors.confirmPassword?.message} />
              </div>
            </FormGroup>
            <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
              <li>
                <Button
                  color="primary"
                  size="lg"
                  type="submit"
                  loading={loader}
                >
                  ویرایش کلمه عبور
                </Button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => setUser({})}
                  className="link link-light"
                >
                  انصراف
                </button>
              </li>
            </ul>
          </Form>
        </div>
      </ModalBody>
    </Modal>
  );
};

const CreateTransaction = () => {
  const loader = useSelector((state) => state.loader);

  const { user, setUser, handleCreateTransaction } =
    useContext(userAdminContext);

  // Validation
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createTransactionForAdminValidation),
  });

  useEffect(() => {
    if (user.isTransaction)
      reset({
        amount: {
          travel: 0,
          service: 0,
          household: 0,
          sustenance: 0,
          entertainment: 0,
          lifestyle: 0,
          education: 0,
          gift: 0,
        },
      });
  }, [user]);

  return (
    <Modal
      isOpen={user.isTransaction}
      className="modal-dialog-centered"
      size="md"
      toggle={() => setUser({})}
    >
      <ModalBody>
        <span type="button" onClick={() => setUser({})} className="close">
          <Icon name="cross-sm" />
        </span>
        <div className="p-2">
          <h5 className="title">
            ایجاد تراکنش برای {user?.firstName} {user?.lastName}
          </h5>
          <br />
          <Form
            className="is-alter"
            onSubmit={handleSubmit(handleCreateTransaction)}
          >
            <Row className="gy-4">
              {categoryItems.map((item) => (
                <Col key={item} sm="6">
                  <FormGroup>
                    <label className="form-label" htmlFor={`amount.${item}`}>
                      مبلغ {convertCategoryToText(item)} <Currency />
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="number"
                        min={0}
                        placeholder={`مبلغ ${convertCategoryToText(
                          item
                        )} را وارد نمایید`}
                        className="form-control-lg form-control"
                        {...register(`amount.${item}`)}
                      />
                      {errors.amount && (
                        <ErrorMessage message={errors.amount[item]?.message} />
                      )}
                      {watch(`amount.${item}`) > 0 && (
                        <p className="mt-1">
                          <ConvertCurrencyToText
                            price={watch(`amount.${item}`)}
                            isCurrency
                          />
                        </p>
                      )}
                    </div>
                  </FormGroup>
                </Col>
              ))}

              <Col size="12">
                <FormGroup>
                  <label className="form-label" htmlFor="type">
                    نوع تراکنش
                  </label>
                  <div className="form-control-wrap">
                    <select
                      className="form-control-lg form-control"
                      {...register("type")}
                    >
                      <option value="" selected hidden>
                        نوع تراکنش را انتخاب نمایید
                      </option>
                      {createTransactionTypeItems.map((item) => (
                        <option
                          key={item}
                          value={item}
                          selected={item === watch("type")}
                        >
                          {convertTransactionTypeToText(item)}
                        </option>
                      ))}
                    </select>
                    <ErrorMessage message={errors.type?.message} />
                  </div>
                </FormGroup>
              </Col>
              <Col size="12">
                <FormGroup>
                  <label className="form-label" htmlFor="description">
                    توضیحات
                  </label>
                  <div className="form-control-wrap">
                    <textarea
                      placeholder="توضیحات را وارد نمایید"
                      className="form-control-lg form-control"
                      {...register("description")}
                    />
                    <ErrorMessage message={errors.description?.message} />
                  </div>
                </FormGroup>
              </Col>
              <Col size="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button
                      color="primary"
                      size="lg"
                      type="submit"
                      loading={loader}
                    >
                      ایجاد تراکنش
                    </Button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => setUser({})}
                      className="link link-light"
                    >
                      انصراف
                    </button>
                  </li>
                </ul>
              </Col>
            </Row>
          </Form>
        </div>
      </ModalBody>
    </Modal>
  );
};
